<script setup lang="ts">
import { TransitionPresets } from '@vueuse/core'
import { ref, watch, useRewards, useBonus, useTransition, useTimeoutFn } from '#imports'

const route = useRoute()

const { iframeUrl, isPromotionLoading, useGetReminder, useGetPendingReward, handlePromotionCookie } = useRewards()
const { useGetPendingBonus, resetPendingBonus } = useBonus()

function onIframeLoad() {
    isPromotionLoading.value = false
    reminderQuery.clear()
}

const { isLoggedIn } = useUserInfo()

const reminderQuery = useGetReminder({
    enabled: () => !route.query.txId
})
const pendingReward = useGetPendingReward()
const pendingBonus = useGetPendingBonus({
    enabled: () => !route.query.txId
})

function focusRefetch() {
    if (!isLoggedIn.value) return

    pendingBonus.refresh()
    pendingReward.refresh()
    reminderQuery.refresh()
}

useRefetchThrottle({
    refetchInput: () => focusRefetch(),
    useFocus: true,
    key: 'promotion-container'
})

const promoCookie = promotionCookie('promotionCookie')

function beforeEnter(el: Element) {
    const htmlEl = el as HTMLElement
    htmlEl.style.height = '0px'
    htmlEl.style.overflow = 'hidden'
}

function enter(el: Element, done: () => void) {
    const htmlEl = el as HTMLElement
    const height = ref(0)

    const output = useTransition(height, {
        duration: 400,
        transition: TransitionPresets.easeOutCubic,
        onFinished: () => {
            htmlEl.style = ''
            unwatch()
            done()
        },
    })

    const unwatch = watch(output, (h) => {
        htmlEl.style.height = `${h}px`
    })

    height.value = htmlEl.scrollHeight
}

async function closePromotionModal() {
    if (pendingReward.data.value?.title) {
        handlePromotionCookie(pendingReward.data.value.title)
    }

    pendingBonus.refresh()
    reminderQuery.refresh()
}

const { start: startDelayedRefresh } = useTimeoutFn(() => {
    pendingReward.refresh()
    pendingBonus.refresh()
    reminderQuery.refresh()
}, 3000, { immediate: false })

watch(() => route.query.txId, (newId, oldId) => {
    // Do not refresh reminder if there wasn't shown a receipt
    if (!oldId || newId) return

    // The receipt is shown for at least 3 seconds, so we await that time
    startDelayedRefresh()
})
</script>

<template>
    <div class="promotion-container">
        <ClientOnly>
            <!-- The modal we're being shown when we have a pending promotion on log in. -->
            <LazyPromotionModal
                v-if="pendingReward.data.value?.Promotion"
                :promotion="pendingReward.data.value"
                @close="closePromotionModal"
            />
            <Transition
                v-if="pendingBonus.data.value && !promoCookie?.includes(pendingBonus.data.value?.id as string)"
                @before-enter="beforeEnter"
                @enter="enter"
            >
                <LazyPromotionBonusMessage :bonus="pendingBonus.data.value" @close="resetPendingBonus" />
                <!-- <div v-if="pendingBonus" ref="bonusElement" class="promotion-bonus-wrap">
                </div> -->
            </Transition>
            <!-- The message in the top we're being shown when we have no pending, but we have a reminder. -->
            <template v-if="!pendingReward.data.value?.Promotion && reminderQuery.data.value?.reminders?.length">
                <template
                    v-for="reminder of reminderQuery.data.value.reminders"
                    :key="
                        (reminder?.PromotionClaim?.id && `PromotionClaim:id:${reminder.PromotionClaim.id}`)
                            || (reminder?.Promotion?.id && `Promotion:id:${reminder.Promotion.id}`)
                            || reminder.title"
                >
                    <Transition
                        @before-enter="beforeEnter"
                        @enter="enter"
                    >
                        <LazyPromotionMessage
                            v-if="reminder?.Promotion && !promoCookie?.includes(reminder.Promotion.id as string)"
                            :promotion="reminder"
                            @close="reminderQuery.close(reminder)"
                        />

                        <!-- If we have an "active" bonus enqueued and we're being told we can activate it. It only sends title, and userMessage. -->
                        <!-- Displays a message to activate a bonus on "My bonuses" -->
                        <LazyPromotionReminder
                            v-else-if="!reminder?.Promotion && reminder?.title && !promoCookie?.includes(reminder.title)"
                            :title="reminder.title"
                            :user-message="reminder.userMessage"
                            @close="reminderQuery.close(reminder)"
                        />
                    </Transition>
                </template>
            </template>

            <!-- When claiming a scratch card or wheel of fortune promotion -->
            <Transition name="fade-in">
                <PromotionIframe
                    v-if="iframeUrl"
                    :src="iframeUrl"
                    :loading="isPromotionLoading"
                    @load="onIframeLoad"
                />
            </Transition>
        </ClientOnly>
    </div>
</template>

<style lang="scss" scoped>
.promotion-container {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin: 0 auto;

    @media (min-width: $screen-lg) {
        max-width: calc($container-lg - 60px);
    }

    @media (min-width: $screen-xl) {
        max-width: calc($container-xl - 60px);
    }

    :deep(.alert:last-child) {
        margin-bottom: 10px;
    }

    :deep(.alert:first-child) {
        margin-top: 10px;
    }
}
</style>
